import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { SiFacebook, SiGoogle } from "react-icons/si";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { (e.target as HTMLElement).classList.add("in"); } }),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

const PORTFOLIO_ITEMS = [
  { name: "dogplaces.app", display: "dogplaces.app", img: "/images/portfolio-dogplaces.png" },
  { name: "goable.app", display: "goable.app", img: "/images/portfolio-goable.png" },
  { name: "bizdr.app", display: "bizdr.app", img: "/images/portfolio-bizdr.png" },
  { name: "kanae.co.nz", display: "kanae.co.nz", img: "/images/portfolio-kanae.png" },
];

const PORTFOLIO_CARDS = [
  {
    name: "dogplaces.app",
    url: "https://dogplaces.app",
    description: "A dog-friendly places directory covering cafés, parks, beaches and stays across Australia.",
    image: "/images/portfolio-dogplaces.png",
    accent: "#6FAFB5",
  },
  {
    name: "goable.app",
    url: "https://goable.app",
    description: "An accessibility and inclusion venue directory helping people find spaces that genuinely welcome them.",
    image: "/images/portfolio-goable.png",
    accent: "#E5B75E",
  },
  {
    name: "bizdr.app",
    url: "https://bizdr.app",
    description: "Our own brand — built by BizDr for BizDr. The site you're looking at right now.",
    image: "/images/portfolio-bizdr.png",
    accent: "#6FAFB5",
  },
  {
    name: "kanae.co.nz",
    url: "https://kanae.co.nz",
    description: "Kanae Consultancy — civil engineering, project management and Māori cultural advisory. Bay of Plenty & Waikato, Aotearoa NZ.",
    image: "/images/portfolio-kanae.png",
    accent: "#c9a84c",
  },
];

const TICKER_ITEMS = [
  "Cafés & restaurants", "Trades & services", "Retail & boutiques",
  "Health & wellness", "Accommodation & stays", "Australia-wide", "Built with care",
  "Cafés & restaurants", "Trades & services", "Retail & boutiques",
  "Health & wellness", "Accommodation & stays", "Australia-wide", "Built with care",
];

export default function Home() {
  useReveal();
  const { toast } = useToast();
  const [formOk, setFormOk] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // ↓ Replace YOUR_FORM_ID with your Formspree form ID (e.g. "xpwzabcd")
  const FORMSPREE_ENDPOINT = "https://formspree.io/f/xykbbnyq";

  const enquiryMutation = useMutation({
    mutationFn: (data: { name: string; email: string; message: string; source: string }) =>
      apiRequest("POST", "/api/enquiry", data),
    onError: () => {
      toast({ title: "Something went wrong", description: "Please try again or email us directly.", variant: "destructive" });
    },
  });

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);

    const businessName  = (fd.get("business")  as string) || "";
    const ownerName     = (fd.get("name")       as string) || "";
    const businessType  = (fd.get("service")    as string) || "";
    const painPoints    = (fd.get("message")    as string) || "";
    const email         = (fd.get("email")      as string) || "";
    const phone         = (fd.get("phone")      as string) || "";
    const extras        = [email && `Email: ${email}`, phone && `Phone: ${phone}`].filter(Boolean).join(", ");

    // 1 — POST to Formspree
    fetch(FORMSPREE_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify({ businessName, ownerName, businessType, location: "", currentOnline: "", mainServices: "", painPoints, goals: "", extras }),
    }).catch(err => console.error("Formspree error:", err));

    // 2 — Open Claude with form data as query params
    const params = new URLSearchParams({ businessName, ownerName, businessType, location: "", currentOnline: "", mainServices: "", painPoints, goals: "", extras });
    window.open(`https://claude.ai/new?q=bizdr_enquiry&${params.toString()}`, "_blank");

    // 3 — Also send via backend for confirmation emails
    enquiryMutation.mutate({
      name: `${ownerName} — ${businessName}`,
      email,
      message: `Phone: ${phone || "not provided"}\nService: ${businessType}\n\n${painPoints}`,
      source: "Contact form",
    });

    // 4 — Show success immediately
    setFormOk(true);
    formRef.current?.reset();
  }

  const year = new Date().getFullYear();

  return (
    <div className="overflow-x-hidden font-sans" style={{ background: "#F7F4EE", color: "#1F2E3B" }}>

      {/* Skip to main content — keyboard/screen-reader users */}
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* ── NAV ── */}
      <nav aria-label="Main navigation" className="sticky top-0 z-50 border-b" style={{ background: "rgba(247,244,238,.92)", backdropFilter: "blur(14px)", borderColor: "#e4ddd2" }}>
        <div className="mx-auto max-w-6xl px-6 py-3 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-2.5" data-testid="link-logo" aria-label="BizDr — home" style={{ textDecoration: "none" }}>
            <svg width="28" height="16" viewBox="0 0 28 16" fill="none" aria-hidden="true" focusable="false">
              <path d="M1 8 L5.5 8 L8 1.5 L10.5 14.5 L13.5 8 L27 8"
                stroke="#6FAFB5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.4rem", letterSpacing: "-0.01em", color: "#1F2E3B", lineHeight: 1 }}>
              Biz<em style={{ fontStyle: "italic", color: "#3a7178" }}>Dr</em>
            </span>
          </a>
          <ul className="hidden md:flex gap-8 list-none" style={{ color: "#1F2E3B" }}>
            {[["#problem", "Why a Website"], ["#process", "How It Works"], ["#work", "My Work"], ["#pricing", "Pricing"], ["#about", "About"], ["#contact", "Contact"], ["/blog", "Free Resources"]].map(([href, label]) => (
              <li key={href}>
                <a href={href} data-testid={`link-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-sm transition-colors hover:text-teal" style={{ color: "#1F2E3B", opacity: 0.7, textDecoration: "none" }}
                  onMouseEnter={e => { (e.target as HTMLElement).style.opacity = "1"; (e.target as HTMLElement).style.color = "#6FAFB5"; }}
                  onMouseLeave={e => { (e.target as HTMLElement).style.opacity = "0.7"; (e.target as HTMLElement).style.color = "#1F2E3B"; }}>
                  {label}
                </a>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-3">
            <a href="/check" data-testid="button-nav-cta"
              className="hidden md:inline-flex text-sm font-medium rounded-full px-5 py-2.5 transition-all hover:-translate-y-0.5"
              style={{ background: "#1F2E3B", color: "#fff", textDecoration: "none" }}>
              Book a BizDr Check
            </a>
            {/* Mobile hamburger */}
            <button
              className="md:hidden flex flex-col justify-center items-center gap-1.5 p-2 rounded-lg transition-colors"
              style={{ background: "transparent", border: "none", cursor: "pointer" }}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              data-testid="button-mobile-menu"
              onClick={() => setMobileMenuOpen(o => !o)}>
              <span style={{ display: "block", width: 22, height: 2, background: "#1F2E3B", borderRadius: 2, transition: "all .2s", transform: mobileMenuOpen ? "translateY(5px) rotate(45deg)" : "none" }} />
              <span style={{ display: "block", width: 22, height: 2, background: "#1F2E3B", borderRadius: 2, transition: "all .2s", opacity: mobileMenuOpen ? 0 : 1 }} />
              <span style={{ display: "block", width: 22, height: 2, background: "#1F2E3B", borderRadius: 2, transition: "all .2s", transform: mobileMenuOpen ? "translateY(-5px) rotate(-45deg)" : "none" }} />
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t" style={{ background: "#F7F4EE", borderColor: "#e4ddd2" }}>
            <ul className="list-none px-6 py-4 flex flex-col gap-1">
              {[["#problem", "Why a Website"], ["#process", "How It Works"], ["#work", "My Work"], ["#pricing", "Pricing"], ["#about", "About"], ["#contact", "Contact"], ["/blog", "Free Resources"]].map(([href, label]) => (
                <li key={href}>
                  <a href={href}
                    data-testid={`link-mobile-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-3 text-sm font-medium border-b"
                    style={{ color: "#1F2E3B", textDecoration: "none", borderColor: "#e4ddd2" }}>
                    {label}
                  </a>
                </li>
              ))}
              <li className="pt-3">
                <a href="/check"
                  data-testid="button-mobile-nav-cta"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-center rounded-full py-3 text-sm font-medium"
                  style={{ background: "#1F2E3B", color: "#fff", textDecoration: "none" }}>
                  Book a BizDr Check
                </a>
              </li>
            </ul>
          </div>
        )}
      </nav>

      {/* ── MAIN CONTENT ── */}
      <main id="main-content">

      {/* ── HERO ── */}
      <section id="top" aria-label="Hero" style={{ overflow: "hidden" }}>
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-28 grid md:grid-cols-2 gap-10 md:gap-16 items-center" style={{ minHeight: "88vh" }}>

          <div>
            <div className="fu text-xs font-medium uppercase tracking-widest mb-4" style={{ color: "#E5B75E" }}>
              Wherever you are in Australia
            </div>

            <h1 className="fu-1 font-serif font-bold mb-5" style={{ fontSize: "clamp(2.4rem,5vw,3.8rem)", lineHeight: 1.12 }} data-testid="text-hero-heading">
              Your business deserves more than a{" "}
              <em style={{ fontStyle: "italic", color: "#4e8f95" }}>social media page.</em>
            </h1>

            <p className="fu-2 text-lg leading-relaxed mb-8 max-w-lg" style={{ color: "#536270" }}>
              BizDr builds proper, professional websites for small businesses wherever they are in Australia. Not from a city office. From the road. Donna and her co-pilot Walt travel Australia town by town, working with real businesses in real communities. Which means when BizDr builds your website, it comes with genuine understanding of what it actually takes to run a small business.
            </p>

            <div className="fu-3 flex flex-wrap gap-3 mb-8">
              <a href="/check" data-testid="button-hero-primary"
                className="rounded-full px-6 py-3 text-sm font-medium transition-all hover:-translate-y-0.5"
                style={{ background: "#6FAFB5", color: "#fff", textDecoration: "none" }}>
                Get a Free Assessment
              </a>
              <a href="#pricing" data-testid="button-hero-secondary"
                className="rounded-full px-6 py-3 text-sm font-medium transition-all hover:-translate-y-0.5"
                style={{ background: "transparent", color: "#1F2E3B", border: "1.5px solid #1F2E3B", textDecoration: "none" }}>
                See Pricing
              </a>
            </div>

            <div className="fu-4 inline-flex items-center gap-3 rounded-2xl px-4 py-3"
              style={{ background: "#fff", border: "1px solid #e4ddd2", boxShadow: "0 2px 12px rgba(31,46,59,.06)" }}>
              <div className="rounded-full flex items-center justify-center text-2xl flex-shrink-0"
                style={{ width: 44, height: 44, background: "linear-gradient(135deg,#6FAFB5,#E5B75E)" }}>🐶</div>
              <div>
                <strong className="block text-sm font-medium" style={{ color: "#1F2E3B" }}>Walt-approved business advice</strong>
                <span className="text-xs" style={{ color: "#536270" }}>Travelling Australia town by town, helping small businesses one at a time.</span>
              </div>
            </div>
          </div>

          {/* ── Hero image — right column ── */}
          <div style={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 24,
            alignSelf: "stretch",
            minHeight: 340,
          }}>
            <img
              src="/images/hero-donna-walt.png"
              alt="Donna and Walt at a café — BizDr on the road"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center top",
                display: "block",
              }}
            />
            {/* Warm left-edge blend into cream background */}
            <div style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(to right, rgba(247,244,238,0.28) 0%, transparent 28%)",
              pointerEvents: "none",
            }} />
          </div>

        </div>
      </section>

      {/* ── PORTFOLIO CAROUSEL ── */}
      <div style={{ overflow: "hidden", position: "relative", padding: "2.5rem 0", background: "#F7F4EE", borderTop: "1px solid #e4ddd2" }}>
        <div style={{ position: "absolute", inset: "0 auto 0 0", width: 140, background: "linear-gradient(to right, #F7F4EE, transparent)", zIndex: 2, pointerEvents: "none" }} />
        <div style={{ position: "absolute", inset: "0 0 0 auto", width: 140, background: "linear-gradient(to left, #F7F4EE, transparent)", zIndex: 2, pointerEvents: "none" }} />
        <div className="portfolio-track flex" style={{ width: "max-content", gap: "1.25rem" }}>
          {[...PORTFOLIO_ITEMS, ...PORTFOLIO_ITEMS, ...PORTFOLIO_ITEMS, ...PORTFOLIO_ITEMS].map((item, i) => (
            <div key={i} style={{ width: 300, flexShrink: 0, background: "#fff", border: "1px solid #e4ddd2", borderRadius: "0.875rem", overflow: "hidden", boxShadow: "0 4px 20px rgba(31,46,59,.07)" }}>
              <div style={{ padding: "0.5rem 0.75rem", borderBottom: "1px solid #e4ddd2", display: "flex", alignItems: "center", gap: 5 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#E5B75E", flexShrink: 0 }} />
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#6FAFB5", flexShrink: 0 }} />
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#e4ddd2", flexShrink: 0 }} />
                <div style={{ flex: 1, background: "#F7F4EE", borderRadius: 99, padding: "2px 9px", fontSize: "0.65rem", color: "#536270" }}>
                  🔒 {item.display}
                </div>
              </div>
              <img src={item.img} alt={item.name} style={{ width: "100%", aspectRatio: "16/8", objectFit: "cover", display: "block" }} />
              <div style={{ padding: "0.45rem 0.75rem 0.6rem", fontSize: "0.75rem", fontWeight: 600, color: "#1F2E3B", fontFamily: "Playfair Display, serif" }}>
                {item.name}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── TICKER ── */}
      <div className="overflow-hidden py-3" style={{ background: "#1F2E3B", borderTop: "1px solid rgba(255,255,255,.05)" }}>
        <div className="ticker-track flex gap-12" style={{ width: "max-content" }}>
          {TICKER_ITEMS.map((item, i) => (
            <span key={i} className="flex items-center gap-2.5 text-sm whitespace-nowrap" style={{ color: "rgba(255,255,255,.7)" }}>
              <span className="rounded-full flex-shrink-0" style={{ width: 5, height: 5, background: "#6FAFB5" }} />
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ── PROBLEM ── */}
      <section id="problem" className="py-20" style={{ background: "#fff", borderTop: "1px solid #e4ddd2", borderBottom: "1px solid #e4ddd2" }}>
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-16 items-center">
          <div className="reveal">
            <div className="text-xs font-medium uppercase tracking-widest mb-4 section-tag">The Problem</div>
            <h2 className="font-serif mb-5" style={{ fontSize: "clamp(1.8rem,3vw,2.6rem)", fontWeight: 500 }}>Social media gets you seen. A website makes you trusted.</h2>
            <p className="leading-relaxed mb-3" style={{ color: "#536270" }}>
              Most small businesses rely entirely on Facebook or Instagram — platforms you don't own, that hide your posts, and that give customers no clear way to understand who you are or how to book with you.
            </p>
            <p className="leading-relaxed mb-8" style={{ color: "#536270" }}>
              A website gives your business a real home. One place that's always open, always on, always yours.
            </p>
            <a href="/check" data-testid="button-problem-cta"
              className="inline-flex rounded-full px-6 py-3 text-sm font-medium transition-all hover:-translate-y-0.5"
              style={{ background: "#6FAFB5", color: "#fff", textDecoration: "none" }}>
              Get a Free Assessment
            </a>
          </div>

          <div className="reveal grid grid-cols-2 gap-4">
            {[
              { num: "1/3", sub: null, label: "Small businesses have no website at all", accent: "#6FAFB5" },
              { num: "70", sub: "%", label: "Of customers check online before visiting a business", accent: "#6FAFB5" },
              { num: "5", sub: "×", label: "More trust when customers find a professional website", accent: "#6FAFB5" },
              { num: "$0", sub: null, label: "Cost to see your BizDr demo — no obligation, ever", accent: "#6FAFB5" },
            ].map(({ num, sub, label }) => (
              <div key={label} className="rounded-2xl p-5" data-testid={`stat-${label.slice(0,10).replace(/\s/g,"-")}`}
                style={{ background: "#F7F4EE", border: "1px solid #e4ddd2" }}>
                <div className="font-serif font-bold mb-1" style={{ fontSize: "2rem", color: "#1F2E3B", lineHeight: 1 }}>
                  {num}{sub && <span style={{ color: "#6FAFB5" }}>{sub}</span>}
                </div>
                <div className="text-xs leading-relaxed" style={{ color: "#536270" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section id="process" className="py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="reveal mb-2">
            <div className="text-xs font-medium uppercase tracking-widest mb-3 section-tag">How It Works</div>
            <h2 className="font-serif" style={{ fontSize: "clamp(1.8rem,3vw,2.6rem)", fontWeight: 500 }}>Three steps. One proper online home.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {[
              { num: "01", title: "Listen", body: "We get to know your business, your story, your customers, and what you're currently working with online. No jargon, no pressure, just an honest conversation about what would actually help." },
              { num: "02", title: "Build", body: "We build a proper, professional website using your real content, photos, and voice. Built accessible from the ground up so everyone who lands on it can use it. You see a live demo before paying a cent." },
              { num: "03", title: "Grow", body: "We connect your socials, set up Google, and stay on as your website person, updating, maintaining, and keeping things running from wherever we are in Australia." },
            ].map(({ num, title, body }) => (
              <div key={num} className="reveal rounded-3xl p-8 relative overflow-hidden transition-all hover:-translate-y-1"
                data-testid={`card-step-${num}`}
                style={{ background: "#fff", border: "1px solid #e4ddd2", boxShadow: "0 2px 0 0 transparent", cursor: "default" }}>
                <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: "linear-gradient(90deg,#6FAFB5,#E5B75E)" }} />
                <div className="font-serif font-bold mb-4" style={{ fontSize: "3rem", color: "#e4ddd2", lineHeight: 1 }}>{num}</div>
                <h3 className="font-serif font-medium text-xl mb-3" style={{ color: "#1F2E3B" }}>{title}</h3>
                <p className="text-sm leading-7" style={{ color: "#536270" }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUR WORK / PORTFOLIO ── */}
      <section id="work" className="py-24" style={{ background: "#1F2E3B" }}>
        <div className="mx-auto max-w-6xl px-6">
          <div className="reveal mb-14">
            <div className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: "#6FAFB5" }}>My Work</div>
            <div className="md:flex items-end justify-between gap-8">
              <div>
                <h2 className="font-serif text-white mb-4" style={{ fontSize: "clamp(1.8rem,3vw,2.6rem)", fontWeight: 500 }}>
                  No two sites are the same.<br />
                  <em style={{ fontStyle: "italic", color: "#E5B75E" }}>Every one is bespoke.</em>
                </h2>
                <p className="max-w-lg" style={{ color: "rgba(255,255,255,.6)", lineHeight: 1.8 }}>
                  I don't use templates. Every site is designed around what that specific business actually needs — its customers, its story, its goals. Here's proof.
                </p>
              </div>
              <a href="#contact" className="inline-flex mt-6 md:mt-0 flex-shrink-0 rounded-full px-6 py-3 text-sm font-medium transition-all hover:-translate-y-0.5"
                style={{ background: "#E5B75E", color: "#1F2E3B", textDecoration: "none" }}>
                Get your own →
              </a>
            </div>
          </div>

          {/* 3-up browser mockup cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {PORTFOLIO_CARDS.map(({ name, url, description, image, accent }) => (
              <div key={name} className="reveal flex flex-col" style={{ borderRadius: 16, overflow: "hidden", background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.09)" }}>

                {/* Browser chrome */}
                <div style={{ background: "rgba(255,255,255,.07)", borderBottom: "1px solid rgba(255,255,255,.08)", padding: "9px 12px", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                  {/* Traffic lights */}
                  <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#FF5F57" }} />
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#FEBC2E" }} />
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#28C840" }} />
                  </div>
                  {/* URL bar */}
                  <div style={{ flex: 1, background: "rgba(255,255,255,.07)", borderRadius: 99, padding: "3px 10px", display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
                    <svg width="9" height="10" viewBox="0 0 9 10" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
                      <rect x="0.75" y="3.5" width="7.5" height="6" rx="1.25" stroke={accent} strokeWidth="1.1"/>
                      <path d="M2.75 3.5V2.75a1.75 1.75 0 013.5 0v.75" stroke={accent} strokeWidth="1.1" strokeLinecap="round"/>
                    </svg>
                    <span style={{ fontSize: "0.65rem", color: "rgba(255,255,255,.55)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", letterSpacing: "0.01em" }}>{name}</span>
                  </div>
                </div>

                {/* Screenshot */}
                <div style={{ overflow: "hidden", height: 190, flexShrink: 0 }}>
                  <img
                    src={image}
                    alt={`${name} website screenshot`}
                    data-testid={`img-portfolio-${name.split(".")[0]}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", display: "block", transition: "transform .4s ease" }}
                    onMouseEnter={e => (e.currentTarget.style.transform = "scale(1.03)")}
                    onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")}
                  />
                </div>

                {/* Card body */}
                <div style={{ padding: "1.25rem 1.25rem 1.5rem", display: "flex", flexDirection: "column", flex: 1, gap: "0.6rem" }}>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.05rem", fontWeight: 500, color: "#fff", margin: 0 }}>{name}</h3>
                  <p style={{ fontSize: "0.82rem", color: "rgba(255,255,255,.55)", lineHeight: 1.65, margin: 0, flex: 1 }}>{description}</p>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "0.5rem", flexWrap: "wrap", gap: "0.5rem" }}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`link-portfolio-${name.split(".")[0]}`}
                      style={{ display: "inline-flex", alignItems: "center", gap: 5, background: accent, color: "#1F2E3B", textDecoration: "none", borderRadius: 999, padding: "7px 16px", fontSize: "0.78rem", fontWeight: 600, transition: "transform .2s", flexShrink: 0 }}
                      onMouseEnter={e => (e.currentTarget.style.transform = "translateY(-2px)")}
                      onMouseLeave={e => (e.currentTarget.style.transform = "")}
                    >
                      Visit Site
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                        <path d="M1.5 8.5L8.5 1.5M8.5 1.5H4M8.5 1.5V6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </a>
                    <span style={{ fontSize: "0.68rem", color: "rgba(255,255,255,.28)", letterSpacing: "0.03em" }}>Built by BizDr</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="reveal text-center text-sm mt-10" style={{ color: "rgba(255,255,255,.3)" }}>
            More sites added as we build them. <a href="#contact" style={{ color: "rgba(255,255,255,.5)", textDecoration: "underline" }}>Yours could be next.</a>
          </p>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section id="pricing" className="py-20" style={{ background: "#fdfcf8", borderTop: "1px solid #e4ddd2", borderBottom: "1px solid #e4ddd2" }}>
        <div className="mx-auto max-w-6xl px-6">
          <div className="reveal">
            <div className="text-xs font-medium uppercase tracking-widest mb-3 section-tag">Pricing</div>
            <h2 className="font-serif mb-2" style={{ fontSize: "clamp(1.8rem,3vw,2.6rem)", fontWeight: 500 }}>Built for your business.<br /><em style={{ fontStyle: "italic", color: "#6FAFB5" }}>Nothing off the shelf.</em></h2>
            <p className="max-w-xl" style={{ color: "#536270" }}>Every site we build is different, because every business is different. Start with a free conversation, and we'll show you exactly what's possible.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-10 items-stretch">
            {/* Free Assessment */}
            <div className="reveal rounded-3xl p-8 transition-all hover:-translate-y-1 flex flex-col" data-testid="card-pricing-starter"
              style={{ background: "#fff", border: "1px solid #e4ddd2" }}>
              <div className="text-xs font-medium uppercase tracking-widest mb-1 section-tag">Step 1</div>
              <h3 className="font-serif font-medium text-xl mb-1" style={{ color: "#1F2E3B" }}>Free Assessment</h3>
              <div className="font-serif font-bold my-4" style={{ fontSize: "2.4rem", lineHeight: 1, color: "#1F2E3B" }}>Free <span className="text-base font-normal opacity-60">always</span></div>
              <p className="text-sm leading-7 mb-5" style={{ color: "#536270" }}>We come to you in person or on a call, learn about your business, and show you exactly what a website could look like. No cost, no pressure, no obligation.</p>
              <ul className="space-y-2 mb-6">
                {["We review your current online presence", "Show you a concept built around your business", "Honest conversation about goals & what's realistic", "No obligation to go any further"].map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm" style={{ color: "#536270" }}>
                    <span className="font-bold mt-0.5 flex-shrink-0" style={{ color: "#6FAFB5" }}>✓</span>{f}
                  </li>
                ))}
              </ul>
              <a href="#contact" data-testid="button-pricing-starter"
                className="block text-center rounded-full py-3 text-sm font-medium transition-all hover:-translate-y-0.5 mt-auto"
                style={{ background: "transparent", color: "#1F2E3B", border: "1.5px solid #1F2E3B", textDecoration: "none" }}>
                Book a Free Assessment
              </a>
            </div>

            {/* Your Website */}
            <div className="reveal rounded-3xl p-8 transition-all hover:-translate-y-1 flex flex-col" data-testid="card-pricing-featured"
              style={{ background: "#1F2E3B", border: "1px solid #1F2E3B" }}>
              <div className="text-xs font-medium uppercase tracking-widest mb-1" style={{ color: "rgba(255,255,255,.5)" }}>Built Just for You</div>
              <h3 className="font-serif font-medium text-xl mb-1 text-white">Your Website</h3>
              <div className="font-serif font-bold my-4 text-white" style={{ fontSize: "2.4rem", lineHeight: 1 }}>$1,500 <span className="text-sm font-normal opacity-60">+ GST</span></div>
              <p className="text-xs mb-4" style={{ color: "rgba(255,255,255,.35)" }}>Most agencies charge $3,000 to $8,000 for the same thing. We don't.</p>
              <p className="text-sm leading-7 mb-5" style={{ color: "rgba(255,255,255,.65)" }}>Designed around your business, your customers, your story, your goals. Not a template with your name dropped in. Yours, from the ground up.</p>
              <ul className="space-y-2 mb-6">
                {["Custom design — no two sites are alike", "Mobile responsive & fast", "Contact or quote form built in", "Google Business Profile setup", "You see it live before you pay a cent"].map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm" style={{ color: "rgba(255,255,255,.65)" }}>
                    <span className="font-bold mt-0.5 flex-shrink-0" style={{ color: "#E5B75E" }}>✓</span>{f}
                  </li>
                ))}
              </ul>
              <a href="#contact" data-testid="button-pricing-featured"
                className="block text-center rounded-full py-3 text-sm font-medium transition-all hover:-translate-y-0.5 mt-auto"
                style={{ background: "#E5B75E", color: "#1F2E3B", textDecoration: "none" }}>
                Let's Build Yours
              </a>
            </div>

            {/* Optional Extras */}
            <div className="reveal rounded-3xl p-8 flex flex-col" data-testid="card-pricing-extras"
              style={{ background: "#F7F4EE", border: "1.5px dashed #d4cdc4" }}>
              <div className="text-xs font-medium uppercase tracking-widest mb-1" style={{ color: "#536270" }}>Also Available</div>
              <h3 className="font-serif font-medium text-xl mb-1" style={{ color: "#1F2E3B" }}>Optional Extras</h3>
              <p className="text-sm mb-6 leading-relaxed" style={{ color: "#536270" }}>Discussed and quoted after your free assessment. Only ever what you actually need.</p>
              <ul className="space-y-3 mb-8 flex-1">
                {[
                  ["Monthly care plan", "Updates, backups & peace of mind"],
                  ["Extra pages", "Gallery, services, team — whatever fits"],
                  ["Content writing", "We write it so you don't have to"],
                  ["SEO health check", "Find out why Google isn't finding you"],
                  ["Accessibility review", "Make sure everyone can use your site"],
                ].map(([title, sub]) => (
                  <li key={title} className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium" style={{ color: "#1F2E3B" }}>{title}</span>
                    <span className="text-xs" style={{ color: "#8a9aa8" }}>{sub}</span>
                  </li>
                ))}
              </ul>
              <a href="#contact" data-testid="button-pricing-extras"
                className="block text-center rounded-full py-3 text-sm font-medium transition-all hover:-translate-y-0.5 mt-auto"
                style={{ background: "transparent", color: "#536270", border: "1.5px solid #d4cdc4", textDecoration: "none" }}>
                Ask at Your Assessment
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── DELIVERABLES ── */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="reveal">
            <div className="text-xs font-medium uppercase tracking-widest mb-3 section-tag">What You Get</div>
            <h2 className="font-serif" style={{ fontSize: "clamp(1.8rem,3vw,2.6rem)", fontWeight: 500 }}>Everything included. Nothing hidden.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {[
              { icon: "🌐", title: "A real website", body: "Yours to own. Not a social media page, not a link-in-bio. A proper home for your business online." },
              { icon: "📱", title: "Works on every device", body: "Phones, tablets, laptops — your site looks great and loads fast wherever your customer is." },
              { icon: "🔗", title: "Socials connected", body: "Facebook, Instagram, TikTok, Google — I link everything so it all works together." },
              { icon: "📍", title: "Google set up", body: "I set up or clean up your Google Business Profile so locals can actually find you." },
              { icon: "✉️", title: "Contact form", body: "Enquiries, bookings, questions — straight to your inbox. No missed DMs." },
              { icon: "♿", title: "Accessibility audit", body: "I review your accessibility details and list you on goable.app — reaching customers who need to know you welcome them." },
              { icon: "🤝", title: "A real human to call on", body: "I don't disappear after launch. I'm your website person, from wherever the caravan is parked." },
            ].map(({ icon, title, body }) => (
              <div key={title} className="reveal rounded-2xl p-6 flex flex-col gap-3" data-testid={`card-deliverable-${title.slice(0,8).replace(/\s/g,"-")}`}
                style={{ background: "#fff", border: "1px solid #e4ddd2" }}>
                <div className="rounded-xl flex items-center justify-center text-2xl" style={{ width: 44, height: 44, background: "#F7F4EE", border: "1px solid #e4ddd2", flexShrink: 0 }}>{icon}</div>
                <h4 className="font-serif font-medium" style={{ fontSize: "1rem", color: "#1F2E3B" }}>{title}</h4>
                <p className="text-sm leading-relaxed" style={{ color: "#536270" }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="py-20" style={{ background: "#1F2E3B" }}>
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-16 items-center">
          <div className="reveal">
            <div className="text-xs font-medium uppercase tracking-widest mb-4" style={{ color: "#E5B75E" }}>About BizDr</div>
            <h2 className="font-serif text-white mb-5" style={{ fontSize: "clamp(1.8rem,3vw,2.6rem)", fontWeight: 500 }}>A business built on the road, for businesses on the ground.</h2>
            <p className="leading-relaxed mb-3" style={{ color: "rgba(255,255,255,.7)" }}>
              BizDr started because we kept driving through beautiful towns across Australia and seeing brilliant small businesses, cafés, trades, shops, with no website, no real online presence, nothing that matched how good they actually were.
            </p>
            <p className="leading-relaxed mb-3" style={{ color: "rgba(255,255,255,.7)" }}>
              So we stopped. We knocked on doors. We showed people what was possible. And they said yes.
            </p>
            <p className="leading-relaxed mb-8" style={{ color: "rgba(255,255,255,.7)" }}>
              Now we travel the country with my caravan, my laptop, and Walt, building websites for the businesses that big agencies overlook. And every site we build is accessible by default, not as a checkbox, not as an add-on, just the way it's done. Because a website that works for everyone isn't a feature. It should be the standard.
            </p>
            <a href="#contact" data-testid="button-about-cta"
              className="inline-flex rounded-full px-6 py-3 text-sm font-medium transition-all hover:-translate-y-0.5"
              style={{ background: "#E5B75E", color: "#1F2E3B", textDecoration: "none" }}>
              Work with me
            </a>
          </div>

          <div className="reveal flex flex-col gap-4">
            <div className="relative" data-testid="img-donna-and-walt">
              <img
                src="/images/donna-and-walt.png"
                alt="Donna and Walt — BizDr founder and her German Shepherd co-pilot, working from a café"
                className="w-full rounded-3xl object-cover"
                style={{ border: "2px solid rgba(255,255,255,.1)", maxHeight: 480 }}
              />
              <div className="absolute bottom-4 left-4 right-4 rounded-2xl px-4 py-3 flex items-center gap-3"
                style={{ background: "rgba(31,46,59,.85)", backdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,.1)" }}>
                <div>
                  <div className="text-white text-sm font-medium">Donna &amp; Walt</div>
                  <div className="text-xs" style={{ color: "rgba(255,255,255,.55)" }}>Founder &amp; Chief Morale Officer</div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl p-5 text-sm" style={{ background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", color: "rgba(255,255,255,.6)" }}>
              🚐 Currently travelling · <strong style={{ color: "#6FAFB5" }}>Next stop: wherever you are</strong><br />
              <span className="text-xs opacity-70">Building websites across Australia — city or country.</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── ECOSYSTEM ── */}
      <section className="py-20" style={{ background: "#fdfcf8", borderTop: "1px solid #e4ddd2" }}>
        <div className="mx-auto max-w-6xl px-6">
          <div className="reveal">
            <div className="text-xs font-medium uppercase tracking-widest mb-3 section-tag">The BizDr Ecosystem</div>
            <h2 className="font-serif mb-3" style={{ fontSize: "clamp(1.8rem,3vw,2.6rem)", fontWeight: 500 }}>I don't just build websites.<br />I build community.</h2>
            <p className="max-w-xl" style={{ color: "#536270" }}>Alongside BizDr, I run two free community directories that connect real people with businesses that welcome them. Get your business listed on both — it costs nothing and reaches the people who matter.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 mt-10">
            {/* dogplaces */}
            <div className="reveal rounded-3xl p-9 flex flex-col gap-5 transition-all hover:-translate-y-1" data-testid="card-ecosystem-dogplaces"
              style={{ background: "#fff", border: "1px solid #e4ddd2" }}>
              <div className="flex items-start justify-between gap-4">
                <div className="rounded-xl flex items-center justify-center text-3xl" style={{ width: 54, height: 54, background: "#FFF7EC", border: "1px solid #f5dfa0" }}>🐾</div>
                <span className="rounded-full px-3 py-1 text-xs font-medium uppercase tracking-wide" style={{ background: "#FFF7EC", color: "#b8832a", border: "1px solid #f5dfa0" }}>Free listing</span>
              </div>
              <div>
                <h3 className="font-serif font-medium text-xl mb-2" style={{ color: "#1F2E3B" }}>dogplaces.app</h3>
                <p className="text-sm leading-relaxed" style={{ color: "#536270" }}>Australia's directory of dog-friendly cafés, parks, beaches, trails, and stays. Rated by real dog owners. Inspected by Walt.</p>
              </div>
              <ul className="space-y-2">
                {["Free listing for dog-friendly businesses", "Searchable by location and category", "Rated and reviewed by the community", "Links directly to your website"].map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm" style={{ color: "#536270" }}>
                    <span className="font-bold mt-0.5 flex-shrink-0" style={{ color: "#6FAFB5" }}>✓</span>{f}
                  </li>
                ))}
              </ul>
              <div className="pt-4 text-sm" style={{ borderTop: "1px solid #e4ddd2", color: "#536270" }}>
                <strong style={{ color: "#1F2E3B" }}>BizDr connection:</strong> Every website we build gets listed on dogplaces if they're dog-friendly — more eyes on your business from day one.
              </div>
              <a href="https://dogplaces.app" target="_blank" rel="noopener noreferrer" data-testid="link-dogplaces"
                className="self-start rounded-full px-5 py-2.5 text-sm font-medium transition-all hover:-translate-y-0.5"
                style={{ background: "#6FAFB5", color: "#fff", textDecoration: "none" }}>
                Visit dogplaces.app 🐾
              </a>
            </div>

            {/* goable */}
            <div className="reveal rounded-3xl p-9 flex flex-col gap-5 transition-all hover:-translate-y-1" data-testid="card-ecosystem-goable"
              style={{ background: "#fff", border: "1px solid #e4ddd2" }}>
              <div className="flex items-start justify-between gap-4">
                <div className="rounded-xl flex items-center justify-center text-3xl" style={{ width: 54, height: 54, background: "#EAF4F5", border: "1px solid #b8dde1" }}>♿</div>
                <span className="rounded-full px-3 py-1 text-xs font-medium uppercase tracking-wide" style={{ background: "#EAF4F5", color: "#4e8f95", border: "1px solid #b8dde1" }}>Free listing</span>
              </div>
              <div>
                <h3 className="font-serif font-medium text-xl mb-2" style={{ color: "#1F2E3B" }}>goable.app</h3>
                <p className="text-sm leading-relaxed" style={{ color: "#536270" }}>Australia's accessibility directory — helping people with disabilities, carers, and support workers find places that genuinely welcome them.</p>
              </div>
              <ul className="space-y-2">
                {["Free listing for accessible businesses", "Detailed accessibility information", "Trusted by people with disabilities & carers", "Useful to local councils & tourism bodies"].map(f => (
                  <li key={f} className="flex items-start gap-2 text-sm" style={{ color: "#536270" }}>
                    <span className="font-bold mt-0.5 flex-shrink-0" style={{ color: "#6FAFB5" }}>✓</span>{f}
                  </li>
                ))}
              </ul>
              <div className="pt-4 text-sm" style={{ borderTop: "1px solid #e4ddd2", color: "#536270" }}>
                <strong style={{ color: "#1F2E3B" }}>BizDr connection:</strong> I can audit and add your accessibility details directly to your website — and get you listed on goable at the same time.
              </div>
              <a href="https://goable.app" target="_blank" rel="noopener noreferrer" data-testid="link-goable"
                className="self-start rounded-full px-5 py-2.5 text-sm font-medium transition-all hover:-translate-y-0.5"
                style={{ background: "#6FAFB5", color: "#fff", textDecoration: "none" }}>
                Visit goable.app ♿
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" className="py-20" style={{ background: "#fff", borderTop: "1px solid #e4ddd2" }}>
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-16 items-start">
          <div className="reveal">
            <div className="text-xs font-medium uppercase tracking-widest mb-3 section-tag">Get in Touch</div>
            <h2 className="font-serif mb-4" style={{ fontSize: "clamp(1.8rem,3vw,2.6rem)", fontWeight: 500 }}>Let's give your business a proper home online.</h2>
            <p className="leading-relaxed mb-8" style={{ color: "#536270" }}>
              Whether you're in a regional town or a city suburb, whether you want a full website, just a check, or you're not sure yet, reach out. No pitch, no pressure. Just a conversation about what would actually help your business.
            </p>
            <div className="flex flex-col gap-4">
              {["You see the demo before paying anything", "We support you remotely from anywhere in Australia", "No lock-in contracts", "Priced fairly for small business"].map(point => (
                <div key={point} className="flex items-center gap-3 text-sm" style={{ color: "#536270" }}>
                  <span className="font-bold" style={{ color: "#6FAFB5", fontSize: "1.1rem" }}>✓</span>
                  {point}
                </div>
              ))}
            </div>
          </div>

          <div className="reveal">
            <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="form-contact">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-name" className="text-xs font-medium" style={{ color: "#1F2E3B" }}>Your name</label>
                  <input id="contact-name" name="name" type="text" placeholder="Jane Smith" required data-testid="input-name"
                    className="rounded-xl px-4 py-3 text-sm outline-none transition-colors"
                    style={{ border: "1.5px solid #e4ddd2", background: "#F7F4EE", color: "#1F2E3B", fontFamily: "DM Sans, sans-serif" }}
                    onFocus={e => (e.target.style.borderColor = "#6FAFB5")}
                    onBlur={e => (e.target.style.borderColor = "#e4ddd2")} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-business" className="text-xs font-medium" style={{ color: "#1F2E3B" }}>Business name</label>
                  <input id="contact-business" name="business" type="text" placeholder="Your business" required data-testid="input-business"
                    className="rounded-xl px-4 py-3 text-sm outline-none transition-colors"
                    style={{ border: "1.5px solid #e4ddd2", background: "#F7F4EE", color: "#1F2E3B", fontFamily: "DM Sans, sans-serif" }}
                    onFocus={e => (e.target.style.borderColor = "#6FAFB5")}
                    onBlur={e => (e.target.style.borderColor = "#e4ddd2")} />
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="contact-email" className="text-xs font-medium" style={{ color: "#1F2E3B" }}>Email</label>
                <input id="contact-email" name="email" type="email" placeholder="you@example.com" required data-testid="input-email"
                  className="rounded-xl px-4 py-3 text-sm outline-none"
                  style={{ border: "1.5px solid #e4ddd2", background: "#F7F4EE", color: "#1F2E3B", fontFamily: "DM Sans, sans-serif" }}
                  onFocus={e => (e.target.style.borderColor = "#6FAFB5")}
                  onBlur={e => (e.target.style.borderColor = "#e4ddd2")} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="contact-phone" className="text-xs font-medium" style={{ color: "#1F2E3B" }}>Phone <span style={{ fontWeight: 400, color: "#536270" }}>(optional)</span></label>
                <input id="contact-phone" name="phone" type="tel" placeholder="0400 000 000" data-testid="input-phone"
                  className="rounded-xl px-4 py-3 text-sm outline-none"
                  style={{ border: "1.5px solid #e4ddd2", background: "#F7F4EE", color: "#1F2E3B", fontFamily: "DM Sans, sans-serif" }}
                  onFocus={e => (e.target.style.borderColor = "#6FAFB5")}
                  onBlur={e => (e.target.style.borderColor = "#e4ddd2")} />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="contact-service" className="text-xs font-medium" style={{ color: "#1F2E3B" }}>What do you need?</label>
                <select id="contact-service" name="service" data-testid="select-service"
                  className="rounded-xl px-4 py-3 text-sm outline-none"
                  style={{ border: "1.5px solid #e4ddd2", background: "#F7F4EE", color: "#1F2E3B", fontFamily: "DM Sans, sans-serif" }}
                  onFocus={e => (e.target.style.borderColor = "#6FAFB5")}
                  onBlur={e => (e.target.style.borderColor = "#e4ddd2")}>
                  <option value="">I'm not sure yet — just curious</option>
                  <option value="assessment">Free Assessment — show me what's possible</option>
                  <option value="starter">Your Website ($1,500 + GST)</option>
                  <option value="chat">Just want to have a chat</option>
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="contact-message" className="text-xs font-medium" style={{ color: "#1F2E3B" }}>Tell us a bit about your business</label>
                <textarea id="contact-message" name="message" placeholder="What do you do? Where are you based? What's the main thing you want your website to do for you?" rows={4} data-testid="textarea-message"
                  className="rounded-xl px-4 py-3 text-sm outline-none resize-vertical"
                  style={{ border: "1.5px solid #e4ddd2", background: "#F7F4EE", color: "#1F2E3B", fontFamily: "DM Sans, sans-serif", minHeight: 120 }}
                  onFocus={e => (e.target.style.borderColor = "#6FAFB5")}
                  onBlur={e => (e.target.style.borderColor = "#e4ddd2")} />
              </div>

              {formOk ? (
                <div className="rounded-2xl p-5 flex items-start gap-4" data-testid="text-form-success"
                  style={{ background: "#f0faf9", border: "1.5px solid #6FAFB5" }}>
                  <span style={{ fontSize: "1.5rem", lineHeight: 1, flexShrink: 0 }}>✓</span>
                  <div>
                    <p className="font-medium text-sm mb-1" style={{ color: "#1F2E3B" }}>Thanks! We'll be in touch shortly.</p>
                    <p className="text-sm" style={{ color: "#536270" }}>Donna is already reviewing your details.</p>
                  </div>
                </div>
              ) : (
                <button type="submit" disabled={enquiryMutation.isPending} data-testid="button-form-submit"
                  className="rounded-full py-3.5 text-sm font-medium transition-all hover:-translate-y-0.5 disabled:opacity-60"
                  style={{ background: "#1F2E3B", color: "#fff", border: "none", cursor: enquiryMutation.isPending ? "not-allowed" : "pointer" }}>
                  {enquiryMutation.isPending ? "Sending…" : "Send Message"}
                </button>
              )}
            </form>
          </div>
        </div>
      </section>

      </main>{/* end #main-content */}

      {/* ── FOOTER ── */}
      <footer style={{ background: "#1F2E3B", padding: "2.5rem 1.75rem" }}>
        <div className="mx-auto max-w-6xl flex flex-col gap-6">

          {/* Centrepiece logo */}
          <div className="flex flex-col items-center gap-2 pb-2" style={{ borderBottom: "1px solid rgba(255,255,255,.08)" }}>
            <img src="/bizdr_logo.png" alt="BizDr" style={{ height: 80, width: 80 }} />
            <div className="text-xs" style={{ color: "rgba(255,255,255,.35)", letterSpacing: "0.08em" }}>Listen · Build · Grow</div>
          </div>

          {/* Top row: logo + nav links */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <svg width="24" height="14" viewBox="0 0 28 16" fill="none" aria-hidden="true" focusable="false">
                  <path d="M1 8 L5.5 8 L8 1.5 L10.5 14.5 L13.5 8 L27 8"
                    stroke="#6FAFB5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.25rem", letterSpacing: "-0.01em", color: "#fff", lineHeight: 1 }}>
                  Biz<em style={{ fontStyle: "italic", color: "#6FAFB5" }}>Dr</em>
                </span>
              </div>
              <div className="text-xs" style={{ color: "rgba(255,255,255,.4)" }}>Listen · Build · Grow</div>
            </div>
            <div className="flex flex-wrap gap-6">
              {[["#problem", "Why a Website"], ["#process", "How It Works"], ["#work", "My Work"], ["#pricing", "Pricing"], ["#about", "About"], ["#contact", "Contact"]].map(([href, label]) => (
                <a key={href} href={href} className="text-xs transition-colors" data-testid={`link-footer-${label.toLowerCase().replace(/\s/g,"-")}`}
                  style={{ color: "rgba(255,255,255,.4)", textDecoration: "none" }}
                  onMouseEnter={e => (e.target as HTMLElement).style.color = "#6FAFB5"}
                  onMouseLeave={e => (e.target as HTMLElement).style.color = "rgba(255,255,255,.4)"}>
                  {label}
                </a>
              ))}
              <a href="/privacy" data-testid="link-footer-privacy"
                className="text-xs transition-colors"
                style={{ color: "rgba(255,255,255,.4)", textDecoration: "none" }}
                onMouseEnter={e => (e.target as HTMLElement).style.color = "#6FAFB5"}
                onMouseLeave={e => (e.target as HTMLElement).style.color = "rgba(255,255,255,.4)"}>
                Privacy Policy
              </a>
              <a href="/privacy#11-accessibility-commitment" data-testid="link-footer-accessibility"
                className="text-xs transition-colors"
                style={{ color: "rgba(255,255,255,.4)", textDecoration: "none" }}
                onMouseEnter={e => (e.target as HTMLElement).style.color = "#6FAFB5"}
                onMouseLeave={e => (e.target as HTMLElement).style.color = "rgba(255,255,255,.4)"}>
                Accessibility
              </a>
            </div>
          </div>

          {/* Social bar */}
          <div className="flex items-center gap-8" style={{ borderTop: "1px solid rgba(255,255,255,.08)", paddingTop: "1.5rem" }}>
            {[
              {
                href: "https://www.facebook.com/profile.php?id=61574308535223",
                icon: <SiFacebook size={22} />,
                label: "Follow us on Facebook",
                testId: "link-social-facebook",
                color: "#1877F2",
              },
              {
                href: "https://share.google/0aHekYyxhz5VRBPx6",
                icon: <SiGoogle size={20} />,
                label: "Find us on Google",
                testId: "link-social-google",
                color: "#00c9a7",
              },
            ].map(({ href, icon, label, testId, color }) => (
              <a key={testId} href={href} target="_blank" rel="noopener noreferrer"
                data-testid={testId}
                className="flex flex-col items-center gap-1.5 group"
                style={{ textDecoration: "none" }}>
                <div className="flex items-center justify-center rounded-full transition-all group-hover:scale-110"
                  style={{ width: 44, height: 44, background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.12)", color }}>
                  {icon}
                </div>
                <span className="text-xs" style={{ color: "rgba(255,255,255,.4)" }}>{label}</span>
              </a>
            ))}
            <div className="ml-auto text-xs" style={{ color: "rgba(255,255,255,.3)" }}>© {year} BizDr. All rights reserved. · ABN 69 491 275 460</div>
          </div>

        </div>
      </footer>
    </div>
  );
}
